import FileUpload from '@mui/icons-material/FileUpload';
import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	Theme,
	useMediaQuery,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FileUploader } from '@volvo/vcdk-react/FileUploader';
import { useSnackbar } from 'notistack';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useProductCenterApi } from 'api/ApplicationApi';
import { healthCheckKeys } from 'api/queries/HealthCheckQueries';
import {
	ALLOWED_UPLOAD_MIME_TYPES,
	MAX_FILE_LIMIT,
	MAX_FILE_SIZE,
} from 'library/components/controls/files/fileUploadValidation';
import { ControlledCommentBox } from 'library/components/controls/form/ControlledCommentBox';
import { COMMENT_BOX_MAX_LENGTH } from 'library/constants/Validations';
import { FileWarningDialog } from 'library/hooks/UseUploadController/FilesWarningDialog';
import { useFileSelectionHandler } from 'library/hooks/UseUploadController/useFileSelectionHandler';

interface HealthCheckFilesFormType {
	comment: string | undefined;
}

type HealthCheckFileUploaderProps = {
	unitId: number;
};

export const HealthCheckFileUploader = ({
	unitId,
}: HealthCheckFileUploaderProps): React.JSX.Element => {
	const { t } = useTranslation([
		'fileUpload',
		'commonWizard',
		'commonButton',
		'common',
		'administration',
	]);

	const { api } = useProductCenterApi();

	const form = useForm<HealthCheckFilesFormType>({
		defaultValues: {
			comment: '',
		},
	});

	const [open, setOpen] = useState(false);

	const {
		filesToUpload,
		fileUploaderError,
		setFileUploaderError,
		invalidFiles,
		warningDialogOpen,
		handleFilesSelected,
		handleFileUploaderError,
		clearFiles,
		warningDialogClose,
	} = useFileSelectionHandler();

	const fullScreen = useMediaQuery((theme: Theme) =>
		theme.breakpoints.only('xs')
	);
	const { enqueueSnackbar } = useSnackbar();
	const queryClient = useQueryClient();

	const uploadMutation = useMutation({
		mutationFn: (payload: { comment: string | undefined }) =>
			api.uploadHealthCheckFiles({
				unitId,
				files: filesToUpload,
				comment: payload.comment,
			}),
	});

	const resetForm = useCallback(() => {
		clearFiles();
		form.reset({ comment: '' });
	}, [clearFiles, form]);

	const close = useCallback(() => {
		setOpen(false);
		resetForm();
	}, [resetForm]);

	const handleUpload = useCallback(async () => {
		if (filesToUpload.length === 0) return;

		setFileUploaderError(null);
		const { comment } = form.getValues();

		try {
			await uploadMutation.mutateAsync({ comment });

			await queryClient.invalidateQueries({
				queryKey: healthCheckKeys.files(unitId),
			});

			enqueueSnackbar(t('administration:upload-pi-sucessful'), {
				variant: 'success',
			});

			close();
		} catch (error) {
			setFileUploaderError(
				(error as Error | undefined)?.message ??
					t('common:error-unexpected')
			);
		}
	}, [
		close,
		enqueueSnackbar,
		filesToUpload.length,
		form,
		queryClient,
		setFileUploaderError,
		t,
		unitId,
		uploadMutation,
	]);

	const isSaveDisabled = useMemo(
		() => uploadMutation.isPending || filesToUpload.length === 0,
		[filesToUpload.length, uploadMutation.isPending]
	);

	return (
		<>
			<Button
				startIcon={<FileUpload />}
				color="secondary"
				disabled={!unitId}
				onClick={() => {
					resetForm();
					setOpen(true);
				}}>
				{t('fileUpload:upload-files-title')}
			</Button>

			{open && (
				<Dialog
					maxWidth={false}
					open={open}
					onClose={close}
					fullScreen={fullScreen}>
					<DialogContent>
						<Box
							sx={{
								display: 'grid',
								gap: 2,
							}}>
							{fileUploaderError && (
								<Alert
									severity="error"
									onClose={() => setFileUploaderError(null)}>
									{fileUploaderError}
								</Alert>
							)}
							<FileUploader
								maxFileSize={MAX_FILE_SIZE}
								required
								multiple
								onFilesRemoved={handleFilesSelected}
								onUploadError={handleFileUploaderError}
								allowedTypes={ALLOWED_UPLOAD_MIME_TYPES}
								label={t('fileUpload:upload-files-title')}
								helperText={t('fileUpload:upload-files-text')}
								onFilesSelected={handleFilesSelected}>
								{t('fileUpload:drag-and-drop-first')}
								<div>
									{t('fileUpload:drag-and-drop-second')}
								</div>
							</FileUploader>

							<ControlledCommentBox
								control={form.control}
								name={'comment'}
								label={t(
									'commonWizard:additional-comment-label'
								)}
								validation={{
									maxLength: {
										value: COMMENT_BOX_MAX_LENGTH,
										message:
											'commonWizard:validation-comment-box-max-length',
									},
								}}
							/>
						</Box>
					</DialogContent>
					<DialogActions>
						<Button variant="outlined" onClick={close}>
							{t('commonButton:cancel')}
						</Button>
						<Button
							color="secondary"
							disabled={isSaveDisabled}
							variant="contained"
							onClick={() => void handleUpload()}>
							{t('commonButton:save')}
						</Button>
					</DialogActions>
				</Dialog>
			)}

			{warningDialogOpen && (
				<FileWarningDialog
					amount={MAX_FILE_LIMIT}
					filteredFiles={invalidFiles}
					onClose={warningDialogClose}
				/>
			)}
		</>
	);
};
